/* eslint-disable @aix/no-console-in-backend -- this IS the logging system, console is the transport layer */
/**
 * Standardized Axiom Logging System
 *
 * This is the single entry point for ALL logs and metrics sent to Axiom.
 * All wrapper functions (blg, logAI, logHTTP, etc.) call emit() internally.
 *
 * Key principles:
 * 1. Minimal types: log, http, ai, db, ext, biz
 * 2. First-class citizens: userId, traceId, requestId (always top-level)
 * 3. Consistent field names (camelCase)
 * 4. Single emit() function for standardization
 *
 * @package @aix/jsutils
 */

import { AIX_ENV, IS_ENV_DEPLOYED } from "@aix/env"
import { trace } from "@opentelemetry/api"
import { createRequestScopedSlot } from "./requestScope"
import { LOG_REDACTED, sanitizeLogRecord } from "./sanitize"
import type { LogString, SafeLogValue } from "./sensitive"

// ============================================================================
// Types
// ============================================================================

/**
 * The 6 core event types - minimal and covers 99% of use cases
 */
export type EventType = "log" | "http" | "ai" | "db" | "ext" | "biz"

export type LogLevel = "debug" | "info" | "warn" | "error"

export type AISubtype = "llm" | "image" | "video"

export type EventStatus = "success" | "error" | "timeout"

/**
 * Base fields present on EVERY event
 */
export interface BaseEntry {
  // === AUTO-FILLED by emit() ===
  _time: number
  type: EventType
  level: LogLevel
  message: string

  // App context (auto from env)
  app: string // "vidmax" | "pebble" | "lunchbreak" | etc.
  env: string // "local" | "dev" | "prod"
  location: string // "backend-vercel" | "backend-render" | "frontend" | "local"

  // === FIRST-CLASS CITIZENS (from request context or passed in) ===
  userId?: string
  traceId?: string
  spanId?: string
  requestId?: string

  // === TYPE-SPECIFIC (optional) ===
  subtype?: string

  // For type="log" only
  caller?: {
    file: string
    function: string
    line: string
    column: string
  }

  // Allow additional fields
  [key: string]: unknown
}

/**
 * Input to emit() - type and message are required, everything else optional
 */
export interface EmitInput {
  type: EventType
  message: string
  level?: LogLevel
  subtype?: string
  userId?: string
  traceId?: string
  spanId?: string
  requestId?: string
  [key: string]: unknown
}

// ============================================================================
// Configuration
// ============================================================================

const AXIOM_ENDPOINT = "https://api.axiom.co"
const MAX_BATCH_SIZE = 10
const FLUSH_INTERVAL_MS = 5000
const VERSION = "2.0.0"

/**
 * Only these fields are allowed at the top level in Axiom.
 * All other custom context is nested under `data` to avoid schema explosion.
 */
const KNOWN_TOP_LEVEL_FIELDS = new Set<string>([
  "_time",
  "type",
  "level",
  "message",
  "app",
  "env",
  "location",
  "data",
  "userId",
  "traceId",
  "spanId",
  "requestId",
  "cronId",
  "runId",
  "subtype",
  "caller",
  "event",
  "method",
  "endpoint",
  "status",
  "duration",
  "durationMs",
  "routerName",
  "procedureName",
  "userAgent",
  "country",
  "provider",
  "model",
  "cost",
  "tokens",
  "operation",
  "dimensions",
  "videoDuration",
  "fps",
  "table",
  "rowCount",
  "queryType",
  "service",
  "error",
  "errorType",
  "errorMessage",
  "errorCode",
  "errorStack",
  "severity",
  "recoverable",
  "feature",
  "value",
  "appId",
  "origin",
  // NOTE: "metadata" intentionally NOT here — it carries arbitrary nested data
  // (Prisma args, Stripe objects, etc.) that would explode Axiom columns.
  // It goes into "data" (map field) instead.
  "retryCount",
  "finishReason",
  "purpose",
  "requestSize",
  "responseSize",
  "httpStatus",
  // Provider-level pricing / margin tracking
  "consumerPrice",
  "providerCost",
  "margin",
])

// ============================================================================
// State
// ============================================================================

let batch: BaseEntry[] = []
let flushTimeout: ReturnType<typeof setTimeout> | null = null

// Request context provider (set by middleware; per request when a scope is active)
const requestContextProvider = createRequestScopedSlot<() => Partial<BaseEntry>>()

// ============================================================================
// Environment Detection
// ============================================================================

function getApp(): string {
  if (typeof process !== "undefined" && process.env) {
    return (
      process.env.NEXT_PUBLIC_OVERWRITE_APP_NAME || process.env.NEXT_PUBLIC_APP_NAME || process.env.APP_NAME || "aix"
    )
  }
  return "aix"
}

function getEnv(): string {
  return AIX_ENV || "local"
}

// Reads VITEST directly rather than @aix/env's isTestEnv: dozens of suites mock @aix/env without it,
// and a missing export on a Vitest mock throws on access.
function isTestRun(): boolean {
  return AIX_ENV === "test" || (typeof process !== "undefined" && process.env?.VITEST === "true")
}

function getLocation(): string {
  if (typeof process === "undefined") return "frontend"

  const env = (process.env || {}) as Record<string, string | undefined>

  if (env.VERCEL) return "backend-vercel"
  if (env.RENDER) return "backend-render"
  if (env.CF_PAGES || env.CLOUDFLARE_WORKER) return "backend-cloudflare"

  return "local"
}

// Publishable ingest-only token, deliberately public (owner ruling 2026-08-10; also ships in Expo eas.json).
const PUBLIC_CLIENT_INGEST_TOKEN = "xaat-dcb421e2-ad83-4f1f-b955-2b029fa0335f"

let warnedMissingServerToken = false

function isClientRuntime(): boolean {
  return typeof window !== "undefined" || (typeof navigator !== "undefined" && navigator.product === "ReactNative")
}

/** Server runtimes must bring their own token; only browsers/apps may use the publishable one. */
function getAxiomToken(): string | undefined {
  if (typeof process !== "undefined") {
    // Priority: AXIOM_INGEST_API_TOKEN > AXIOM_API_TOKEN > client fallback
    if (process.env?.AXIOM_INGEST_API_TOKEN) {
      return process.env.AXIOM_INGEST_API_TOKEN
    }
    if (process.env?.AXIOM_API_TOKEN) {
      return process.env.AXIOM_API_TOKEN
    }
  }
  return isClientRuntime() ? PUBLIC_CLIENT_INGEST_TOKEN : undefined
}

function getAxiomDataset(): string {
  if (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_AXIOM_DATASET) {
    return process.env.NEXT_PUBLIC_AXIOM_DATASET
  }
  return getApp()
}

// ============================================================================
// Caller Info (for type="log" only)
// ============================================================================

function getCallerInfo(): BaseEntry["caller"] | undefined {
  try {
    const err = new Error()
    const stack = err.stack?.split("\n")

    if (!stack) return undefined

    // Skip: Error, getCallerInfo, emit, blg.* wrapper
    // Typically line 4-5 is the actual caller
    for (let i = 3; i < Math.min(stack.length, 7); i++) {
      const line = stack[i]
      if (!line) continue

      // Skip internal emit/blg functions
      if (line.includes("emit.ts") || line.includes("/emit")) continue
      if (line.includes("logging.ts")) continue

      // Parse: "    at FunctionName (file:line:column)"
      const match = /at\s+(.*)\s+\((.*):(\d+):(\d+)\)/.exec(line)
      if (match?.[1] && match[2] && match[3] && match[4]) {
        return {
          function: match[1],
          file: match[2],
          line: match[3],
          column: match[4],
        }
      }

      // Handle anonymous: "    at file:line:column"
      const anonMatch = /at\s+(.*):(\d+):(\d+)/.exec(line)
      if (anonMatch?.[1] && anonMatch[2] && anonMatch[3]) {
        return {
          function: "<anonymous>",
          file: anonMatch[1],
          line: anonMatch[2],
          column: anonMatch[3],
        }
      }
    }
    // eslint-disable-next-line @aix/no-exception-swallowing -- Logging infrastructure cannot self-report to Sentry; errors are retried
  } catch {
    // Ignore errors in caller detection
  }

  return undefined
}

// ============================================================================
// Request Context
// ============================================================================

/**
 * Set the function that retrieves request context
 * Called by middleware (tRPC, Hono) at request start
 */
export function setRequestContextProvider(fn: () => Partial<BaseEntry>): void {
  requestContextProvider.set(fn)
}

/**
 * Clear the request context provider (call at request end)
 */
export function clearRequestContextProvider(): void {
  requestContextProvider.set(null)
}

/**
 * Get current request context (if available)
 */
function getRequestContext(): Partial<BaseEntry> {
  const getRequestContextFn = requestContextProvider.get()
  if (getRequestContextFn) {
    try {
      return getRequestContextFn()
    } catch {
      return {}
    }
  }
  return {}
}

function getActiveOtelContext(): Pick<BaseEntry, "traceId" | "spanId"> {
  const spanContext = trace.getActiveSpan()?.spanContext()
  if (!spanContext) {
    return {}
  }

  if (/^0+$/.test(spanContext.traceId) || /^0+$/.test(spanContext.spanId)) {
    return {}
  }

  return {
    traceId: spanContext.traceId,
    spanId: spanContext.spanId,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

// ============================================================================
// Core: emit()
// ============================================================================

/**
 * The single entry point for ALL logs and metrics.
 * All wrapper functions call this internally.
 *
 * @example
 * emit({ type: "log", message: "User logged in", userId: "123" })
 * emit({ type: "ai", subtype: "llm", message: "[LLM] gpt-4o", cost: 0.003, ... })
 */
export function emit(input: EmitInput): void {
  // Sanitize before spreads, console, buffering, JSON serialization and retries.
  input = sanitizeLogRecord(input) as EmitInput
  const requestContext = sanitizeLogRecord(getRequestContext()) as Partial<BaseEntry>
  const activeOtelContext = getActiveOtelContext()

  const entry: BaseEntry = {
    // Auto-filled
    _time: Date.now(),
    type: input.type,
    message: input.message,
    app: getApp(),
    env: getEnv(),
    location: getLocation(),

    // Defaults
    level: input.level ?? "info",

    // First-class citizens from request context (can be overridden by input)
    userId: requestContext.userId,
    traceId: activeOtelContext.traceId ?? requestContext.traceId,
    spanId: activeOtelContext.spanId ?? requestContext.spanId,
    requestId: requestContext.requestId,

    // Caller info for logs only
    ...(input.type === "log" ? { caller: getCallerInfo() } : {}),
  }

  const mergedInput: Record<string, unknown> = {
    ...requestContext,
    ...activeOtelContext,
    ...input,
  }

  const dynamicData: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(mergedInput)) {
    if (key === "data") {
      if (isRecord(value)) {
        Object.assign(dynamicData, value)
      } else if (value !== undefined) {
        dynamicData[key] = value
      }
      continue
    }

    if (KNOWN_TOP_LEVEL_FIELDS.has(key)) {
      entry[key] = value
      continue
    }

    dynamicData[key] = value
  }

  if (Object.keys(dynamicData).length > 0) {
    entry.data = dynamicData
  }

  batch.push(entry)

  // Running on a dev machine: also log to console. Gate on the HOST, not the env name — apps
  // whose local stack points at shared infra run with AIX_ENV=dev (VidMax does), and gating on
  // AIX_ENV===LOCAL made every blg.* call invisible in their terminal while still shipping to
  // Axiom. Deployed hosts stay console-silent.
  if (!IS_ENV_DEPLOYED) {
    const color = entry.level === "error" ? "\x1b[31m" : entry.level === "warn" ? "\x1b[33m" : "\x1b[36m"
    console.log(
      `${color}[${entry.type}]${entry.level !== "info" ? ` ${entry.level.toUpperCase()}` : ""}\x1b[0m`,
      entry.message,
    )
  }

  // Auto-flush if batch is full
  if (batch.length >= MAX_BATCH_SIZE) {
    void flush()
  }

  // Schedule flush if not already scheduled
  if (!flushTimeout && typeof process !== "undefined") {
    flushTimeout = setTimeout(() => {
      void flush()
      flushTimeout = null
    }, FLUSH_INTERVAL_MS)
  }
}

// ============================================================================
// Flush
// ============================================================================

/**
 * Flush all pending logs to Axiom
 * Call this before process exit or at end of request
 */
export async function flush(): Promise<void> {
  if (batch.length === 0) return

  const toSend = batch
  batch = []

  if (flushTimeout) {
    clearTimeout(flushTimeout)
    flushTimeout = null
  }

  // Test runs share the deployed dataset, so their fixture events read as real prod scheduler and
  // request telemetry. Console output above still shows them locally.
  if (isTestRun()) return

  const dataset = getAxiomDataset()
  const token = getAxiomToken()
  if (!token) {
    // Dev machines already echo every event to the console; warn once rather than per batch.
    if (!warnedMissingServerToken) {
      warnedMissingServerToken = true
      console.warn("[emit] AXIOM_INGEST_API_TOKEN is not set; server logs are not shipped to Axiom")
    }
    return
  }

  try {
    const url = `${AXIOM_ENDPOINT}/v1/datasets/${dataset}/ingest`
    const body = toSend.map((entry) => JSON.stringify(entry)).join("\n")

    const response = await fetch(url, {
      method: "POST",
      body,
      headers: {
        "Content-Type": "application/x-ndjson",
        Authorization: `Bearer ${token}`,
        "User-Agent": `aix-emit/${VERSION}`,
      },
      signal: AbortSignal.timeout(10000),
    })

    if (!response.ok) {
      console.error(`[emit] Axiom error: ${response.status}`)

      if (response.status >= 400 && response.status < 500) {
        // 4xx errors (schema explosion, bad data) won't succeed on retry.
        // Log the original messages to console so they're not lost.
        for (const entry of toSend) {
          console.log(`[emit:fallback] ${entry.type} ${entry.level} ${entry.message}`, entry.data ?? "")
        }
      } else {
        // 5xx: server error, retry makes sense
        batch.unshift(...toSend)
      }
    }
    // eslint-disable-next-line @aix/no-exception-swallowing -- Logging infrastructure cannot self-report to Sentry; errors are retried
  } catch {
    console.warn("[emit] Failed to send to Axiom; batch retained for retry")
    // Put back for retry
    batch.unshift(...toSend)
  }
}

// ============================================================================
// Wrapper: blg (Backend Logging)
// ============================================================================

/**
 * Backend logging API - the primary way to log in application code
 *
 * @example
 * blg.info("User created", { email: "test@example.com" })
 * blg.error("Failed to process", { error: err.message, videoId: "123" })
 */
// Keep legacy primitive/variadic calls typed without coercing objects or invoking getters.
// New callers should use a literal event name and an explicit safe context projection.
const logAtLevel =
  (level: LogLevel) =>
  <Args extends [unknown, ...unknown[]]>(...args: Args & SafeLogValue<Args>): void => {
    const [message, ...context] = args
    const value = context[0]
    const structured = context.length === 1 && value !== null && typeof value === "object" && !Array.isArray(value)
    // Positional strings have no semantic key; never assume they are public metadata.
    const data = structured
      ? sanitizeLogRecord(value)
      : sanitizeLogRecord({ context: context.map((item) => (typeof item === "string" ? LOG_REDACTED : item)) })
    if (typeof message === "string") {
      emit({ ...data, type: "log", level, message })
    } else {
      emit({ ...data, type: "log", level, message: "Legacy log event", value: message })
    }
  }

export const blg = {
  /** Legacy info-level alias; retains the same classification and runtime boundary. */
  log: logAtLevel("info"),
  debug: logAtLevel("debug"),
  info: logAtLevel("info"),
  warn: logAtLevel("warn"),
  error: logAtLevel("error"),

  /**
   * Structured OPS event for system health monitoring.
   * Query: `vidmax | where message contains "[OPS]"`
   * Or by event: `vidmax | where event == "VIDEO_GENERATED"`
   */
  ops: <T extends object>(event: LogString, data?: T & SafeLogValue<T>) => {
    emit({ ...sanitizeLogRecord(data ?? {}), type: "log", level: "info", message: `[OPS] ${event}`, event })
  },

  /**
   * Structured MONITOR event for failure alerting.
   * Query: `vidmax | where message contains "[MONITOR]"`
   */
  monitor: <T extends object>(event: LogString, data?: T & SafeLogValue<T>) => {
    emit({ ...sanitizeLogRecord(data ?? {}), type: "log", level: "error", message: `[MONITOR] ${event}`, event })
  },

  /** @deprecated Use blg.ops() instead. */
  metric: <T extends object>(event: LogString, data?: T & SafeLogValue<T>) => {
    emit({ ...sanitizeLogRecord(data ?? {}), type: "log", level: "info", message: `[OPS] ${event}`, event })
  },
}

// ============================================================================
// Wrapper: logHTTP
// ============================================================================

export interface LogHTTPParams {
  method: string
  endpoint: string
  status: number
  duration: number
  // Optional
  routerName?: string
  procedureName?: string
  userAgent?: string
  country?: string
  error?: string
  errorType?: string
  requestSize?: number
  responseSize?: number
  // Optional request telemetry (land under `data.*` except the first-class fields)
  userId?: string
  requestId?: string
  /** URL path. `endpoint` is redacted by the sanitizer, so queryable paths also go here. */
  path?: string
  durationMs?: number
  ok?: boolean
  /** Every tRPC `router.procedure` in the request (batched URLs carry several) */
  procedures?: string[]
  dbQueryCount?: number
  dbMs?: number
  /** Shared-cache reads as `key:state` */
  sharedCache?: string[]
  colo?: string
  cfRay?: string
  isScheduled?: boolean
  cron?: string
  /** Scheduled run: names of jobs that failed this tick */
  failedJobs?: string[]
}

/**
 * Log an HTTP request/response (usually called by middleware)
 */
export function logHTTP(params: LogHTTPParams): void {
  const level: LogLevel = params.status >= 500 ? "error" : params.status >= 400 ? "warn" : "info"

  emit({
    type: "http",
    level,
    ...params,
    message: "HTTP request completed",
  })
}

// ============================================================================
// Wrapper: logAI (LLM, Image, Video)
// ============================================================================

export interface LogAIParams {
  subtype: AISubtype
  provider: string
  model: string
  duration: number
  cost: number
  status: EventStatus
  // LLM-specific
  tokens?: {
    prompt: number
    completion: number
    total: number
  }
  operation?: string
  // Image/Video-specific
  dimensions?: {
    width: number
    height: number
  }
  videoDuration?: number
  fps?: number
  // Optional
  purpose?: string
  error?: string
  errorType?: string
  retryCount?: number
  finishReason?: string
}

/**
 * Log an AI call (LLM, image generation, video generation)
 *
 * @example
 * logAI({
 *   subtype: "llm",
 *   provider: "openai",
 *   model: "gpt-4o",
 *   duration: 1200,
 *   cost: 0.003,
 *   status: "success",
 *   tokens: { prompt: 100, completion: 50, total: 150 }
 * })
 */
export function logAI(params: LogAIParams): void {
  const level: LogLevel = params.status === "error" ? "error" : params.status === "timeout" ? "warn" : "info"

  const costStr = params.cost > 0 ? ` $${params.cost.toFixed(4)}` : ""
  const tokensStr = params.tokens ? ` (${params.tokens.total} tokens)` : ""

  emit({
    type: "ai",
    level,
    message: `[${params.subtype.toUpperCase()}] ${params.provider}/${params.model}${tokensStr}${costStr}`,
    ...params,
  })
}

// ============================================================================
// Wrapper: logDB
// ============================================================================

export interface LogDBParams {
  table: string
  operation: "select" | "insert" | "update" | "delete" | "transaction"
  duration: number
  // Optional
  rowCount?: number
  queryType?: string // "findMany", "create", etc.
  error?: string
  errorType?: string
}

/**
 * Log a database query (usually called by Prisma middleware)
 */
export function logDB(params: LogDBParams): void {
  const level: LogLevel = params.error ? "error" : params.duration > 1000 ? "warn" : "info"

  emit({
    type: "db",
    level,
    message: `${params.operation.toUpperCase()} ${params.table} (${params.duration}ms, ${params.rowCount ?? "?"} rows)`,
    ...params,
  })
}

// ============================================================================
// Wrapper: logExt (External APIs)
// ============================================================================

export interface LogExtParams {
  service: string // "stripe" | "replicate" | "slack" | "undetectable" | etc.
  endpoint: string
  method: string
  status: number
  duration: number
  // Optional
  cost?: number
  error?: string
  errorType?: string
  retryCount?: number
  requestSize?: number
  responseSize?: number
}

/**
 * Log an external API call (Stripe, Replicate, Slack, etc.)
 */
export function logExt(params: LogExtParams): void {
  const level: LogLevel = params.status >= 500 ? "error" : params.status >= 400 ? "warn" : "info"

  emit({
    type: "ext",
    level,
    ...params,
    message: "External request completed",
  })
}

// ============================================================================
// Wrapper: logBiz (Business Events)
// ============================================================================

export interface LogBizParams {
  event: string // "signup" | "purchase" | "video_created" | "subscription_cancelled" | etc.
  // Optional
  feature?: string
  value?: number // For revenue/count events
  metadata?: Record<string, unknown>
}

/**
 * Log a business event (signups, purchases, conversions, etc.)
 *
 * @example
 * logBiz({ event: "video_created", feature: "ai_generation", value: 1 })
 * logBiz({ event: "subscription_started", value: 29.99, metadata: { plan: "pro" } })
 */
export function logBiz(params: LogBizParams): void {
  emit({
    type: "biz",
    level: "info",
    message: `[BIZ] ${params.event}${params.feature ? ` (${params.feature})` : ""}`,
    ...params,
  })
}

// ============================================================================
// Convenience: logError (structured error event)
// ============================================================================

export interface LogErrorParams {
  errorType: string
  errorMessage: string
  // Optional
  errorStack?: string
  errorCode?: string
  severity?: "low" | "medium" | "high" | "critical"
  recoverable?: boolean
  endpoint?: string
  file?: string
  line?: number
  metadata?: Record<string, unknown>
}

/**
 * Log a structured error event
 * Use this for errors you want to monitor/alert on, not just log
 *
 * @example
 * logError({
 *   errorType: "PaymentFailed",
 *   errorMessage: "Card declined",
 *   severity: "high",
 *   metadata: { stripeError: error.code }
 * })
 */
export function logError(params: LogErrorParams): void {
  emit({
    type: "log", // Errors are logs with level=error
    level: "error",
    subtype: "error",
    ...params,
    message: "Application error reported",
  })
}

// ============================================================================
// Exports
// ============================================================================

export { flush as logFlush }
